import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import NotificationsApi from "../services/notificationsApi";
import { useAuth } from "../hooks/auth";

const NotificationsContext = createContext({
  unread: 0,
  unreadLoading: false,
  refreshUnread: () => {},
  panelOpen: false,
  openPanel: () => {},
  closePanel: () => {},
  list: [],
  listLoading: false,
  bulkLoading: false,
  listError: null,
  hasMore: false,
  loadMore: () => {},
  markRead: () => {},
  markUnread: () => {},
  markVisibleRead: () => {},
  markAllRead: () => {},
  filters: { unreadOnly: false, type: null, severity: null },
  setFilters: () => {},
  typeOptions: [],
  navigateToSync: () => {},
});

const normalizeNotification = (n) => {
  const id = String(n?.id || "").trim();
  const title = n?.title != null ? String(n.title) : "";
  const message = n?.message != null ? String(n.message) : n?.body != null ? String(n.body) : "";
  const createdAt = n?.createdAt != null ? String(n.createdAt) : n?.created_at != null ? String(n.created_at) : "";
  const isRead =
    typeof n?.isRead === "boolean"
      ? n.isRead
      : typeof n?.read === "boolean"
        ? n.read
        : typeof n?.is_read === "boolean"
          ? n.is_read
          : false;
  const type = n?.type != null ? String(n.type) : null;
  const severity = n?.severity != null ? String(n.severity) : null;
  const syncId =
    n?.syncId != null
      ? String(n.syncId)
      : n?.SyncId != null
        ? String(n.SyncId)
        : n?.entityId != null
          ? String(n.entityId)
          : n?.EntityId != null
            ? String(n.EntityId)
            : null;
  const metadataJson = n?.metadataJson ?? n?.MetadataJson ?? null;

  return { raw: n, id, title, message, createdAt, isRead, type, severity, syncId, metadataJson };
};

const parseMetadata = (metadataJson) => {
  if (!metadataJson) return null;
  if (typeof metadataJson === "object") return metadataJson;
  const text = String(metadataJson || "").trim();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const getNotificationSyncId = (n) => {
  if (n?.syncId) return n.syncId;
  const meta = parseMetadata(n?.metadataJson);
  const candidate = meta?.syncId ?? meta?.SyncId ?? meta?.synchronizationId ?? meta?.SynchronizationId ?? null;
  return candidate ? String(candidate) : null;
};

const coerceNotificationsResponse = (data) => {
  const raw = data?.response != null ? data.response : data;
  if (Array.isArray(raw)) return { items: raw, total: null };
  const items = Array.isArray(raw?.items)
    ? raw.items
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.data?.items)
        ? raw.data.items
        : [];
  const total = Number.isFinite(Number(raw?.total ?? raw?.data?.total)) ? Number(raw?.total ?? raw?.data?.total) : null;
  return { items, total };
};

export const NotificationsProvider = ({ children, locationPathname, onNavigateToPath }) => {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [unreadLoading, setUnreadLoading] = useState(false);
  const unreadInFlight = useRef(null);

  const [panelOpen, setPanelOpen] = useState(false);
  const [filters, setFilters] = useState({ unreadOnly: false, type: null, severity: null });
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(false);
  const listInFlight = useRef(null);
  const lastQueryKey = useRef("");
  const loadedQueryKeys = useRef(new Set());
  const panelWasOpen = useRef(false);

  const refreshUnread = useCallback(async () => {
    if (unreadInFlight.current) return unreadInFlight.current;
    setUnreadLoading(true);
    const p = Promise.resolve()
      .then(async () => {
        const data = await NotificationsApi.getUnreadCount();
        const next = Number(data?.unread ?? 0);
        setUnread(Number.isFinite(next) && next >= 0 ? next : 0);
      })
      .finally(() => {
        setUnreadLoading(false);
        unreadInFlight.current = null;
      });
    unreadInFlight.current = p;
    return p;
  }, []);

  const queryKey = useMemo(() => {
    const unreadOnly = filters.unreadOnly ? "1" : "0";
    const type = filters.type ? String(filters.type) : "";
    const severity = filters.severity ? String(filters.severity) : "";
    return `${unreadOnly}|${type}|${severity}|${pageSize}`;
  }, [filters, pageSize]);

  const loadPage = useCallback(
    async (nextPage, mode) => {
      const effectiveMode = mode || "replace";
      if (listInFlight.current) return;
      setListLoading(true);
      setListError(null);

      const payload = {
        page: nextPage,
        pageSize,
        unreadOnly: Boolean(filters.unreadOnly),
        type: filters.type || null,
        severity: filters.severity || null,
      };

      const p = Promise.resolve()
        .then(async () => {
          const data = await NotificationsApi.getAllNotifications(payload);
          const { items, total } = coerceNotificationsResponse(data);
          const normalized = (items || []).map(normalizeNotification).filter((n) => n.id);

          setList((prev) => {
            const base = effectiveMode === "append" ? prev : [];
            const byId = new Map(base.map((x) => [x.id, x]));
            for (const n of normalized) byId.set(n.id, n);
            return Array.from(byId.values());
          });

          setPage(nextPage);
          if (typeof total === "number") {
            const loaded = (effectiveMode === "append" ? list.length : 0) + normalized.length;
            setHasMore(loaded < total);
          } else {
            setHasMore(normalized.length === pageSize);
          }
        })
        .catch((error) => {
          setListError(error);
        })
        .finally(() => {
          setListLoading(false);
          listInFlight.current = null;
        });

      listInFlight.current = p;
      await p;
    },
    [filters, list.length, pageSize],
  );

  const loadFirstPage = useCallback(async () => {
    lastQueryKey.current = queryKey;
    loadedQueryKeys.current.add(queryKey);
    await loadPage(1, "replace");
  }, [loadPage, queryKey]);

  const loadMore = useCallback(async () => {
    if (!hasMore || listLoading || listError) return;
    await loadPage(page + 1, "append");
  }, [hasMore, listError, listLoading, loadPage, page]);

  const openPanel = useCallback(() => {
    setPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
  }, []);

  // Depende de user?.id para recarregar a contagem quando o usuário muda. Antes isso vinha de
  // carona no efeito que tentava abrir um stream SSE de unread-count — mas /notifications/unread-count
  // sempre respondeu JSON, nunca text/event-stream, então aquele caminho falhava na primeira
  // tentativa e caía neste polling. Era uma requisição desperdiçada por carregamento de página.
  useEffect(() => {
    refreshUnread();
  }, [refreshUnread, user?.id]);

  useEffect(() => {
    if (!locationPathname) return;
    refreshUnread();
  }, [locationPathname, refreshUnread]);

  useEffect(() => {
    const id = setInterval(() => refreshUnread(), 45000);
    return () => clearInterval(id);
  }, [refreshUnread]);

  useEffect(() => {
    if (!panelOpen) {
      panelWasOpen.current = false;
      return;
    }

    const openedNow = !panelWasOpen.current;
    const queryChanged = Boolean(lastQueryKey.current) && lastQueryKey.current !== queryKey;

    if (openedNow) {
      panelWasOpen.current = true;
      refreshUnread();
      if (!listInFlight.current) loadFirstPage();
      return;
    }

    if (queryChanged) {
      refreshUnread();
      if (!listInFlight.current) loadFirstPage();
    }
  }, [loadFirstPage, panelOpen, queryKey, refreshUnread]);

  const markRead = useCallback(
    async (id) => {
      const targetId = String(id || "").trim();
      if (!targetId) return;
      const before = list.find((n) => n.id === targetId);
      const wasUnread = before && !before.isRead;
      setList((prev) => prev.map((n) => (n.id === targetId ? { ...n, isRead: true } : n)));
      if (wasUnread) setUnread((v) => Math.max(0, Number(v || 0) - 1));

      try {
        await NotificationsApi.markNotificationRead(targetId);
      } catch {
        setList((prev) => prev.map((n) => (n.id === targetId ? { ...n, isRead: Boolean(before?.isRead) } : n)));
        if (wasUnread) setUnread((v) => Number(v || 0) + 1);
      }
    },
    [list],
  );

  const markUnread = useCallback(
    async (id) => {
      const targetId = String(id || "").trim();
      if (!targetId) return;
      const before = list.find((n) => n.id === targetId);
      const wasRead = before && before.isRead;
      setList((prev) => prev.map((n) => (n.id === targetId ? { ...n, isRead: false } : n)));
      if (wasRead) setUnread((v) => Number(v || 0) + 1);

      try {
        await NotificationsApi.markNotificationUnread(targetId);
      } catch {
        setList((prev) => prev.map((n) => (n.id === targetId ? { ...n, isRead: Boolean(before?.isRead) } : n)));
        if (wasRead) setUnread((v) => Math.max(0, Number(v || 0) - 1));
      }
    },
    [list],
  );

  const markVisibleRead = useCallback(async () => {
    const ids = list.filter((n) => n.id && !n.isRead).map((n) => n.id);
    if (!ids.length) return;

    setList((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, isRead: true } : n)));
    setUnread((v) => Math.max(0, Number(v || 0) - ids.length));

    try {
      await NotificationsApi.markNotificationsReadBatch(ids);
    } catch {
      refreshUnread();
      if (panelOpen) loadFirstPage();
    }
  }, [list, loadFirstPage, panelOpen, refreshUnread]);

  const markAllRead = useCallback(async () => {
    if (bulkLoading) return;
    setBulkLoading(true);
    try {
      const ids = [];
      const maxPages = 50;
      const fetchPageSize = 100;

      for (let p = 1; p <= maxPages; p += 1) {
        const data = await NotificationsApi.getAllNotifications({
          page: p,
          pageSize: fetchPageSize,
          unreadOnly: true,
          type: null,
          severity: null,
        });
        const { items } = coerceNotificationsResponse(data);
        const pageIds = (items || [])
          .map((n) => String(n?.id || "").trim())
          .filter(Boolean);

        if (!pageIds.length) break;
        ids.push(...pageIds);
        if (pageIds.length < fetchPageSize) break;
      }

      if (!ids.length) {
        setUnread(0);
        return;
      }

      const chunkSize = 200;
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        await NotificationsApi.markNotificationsReadBatch(chunk);
      }

      const idSet = new Set(ids);
      setList((prev) => prev.map((n) => (idSet.has(n.id) ? { ...n, isRead: true } : n)));
      setUnread(0);
      loadedQueryKeys.current.clear();
      if (panelOpen) loadFirstPage();
    } catch {
      refreshUnread();
      if (panelOpen) loadFirstPage();
    } finally {
      setBulkLoading(false);
    }
  }, [bulkLoading, loadFirstPage, panelOpen, refreshUnread]);

  const typeOptions = useMemo(() => {
    const set = new Set();
    for (const n of list) if (n.type) set.add(n.type);
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [list]);

  const navigateFromNotification = useCallback(
    (n) => {
      const syncId = getNotificationSyncId(n);
      if (syncId) return `/synchronizations/settings/${syncId}`;
      return null;
    },
    [],
  );

  const navigateToSync = useCallback(
    (n) => {
      const path = navigateFromNotification(n);
      if (path && onNavigateToPath) onNavigateToPath(path);
    },
    [navigateFromNotification, onNavigateToPath],
  );

  const value = useMemo(
    () => ({
      unread,
      unreadLoading,
      refreshUnread,
      panelOpen,
      openPanel,
      closePanel,
      list,
      listLoading,
      bulkLoading,
      listError,
      hasMore,
      loadMore,
      markRead,
      markUnread,
      markVisibleRead,
      markAllRead,
      filters,
      setFilters,
      typeOptions,
      loadFirstPage,
      navigateToSync,
    }),
    [
      closePanel,
      filters,
      hasMore,
      list,
      listError,
      listLoading,
      bulkLoading,
      loadMore,
      markRead,
      markUnread,
      markVisibleRead,
      markAllRead,
      navigateToSync,
      openPanel,
      panelOpen,
      refreshUnread,
      typeOptions,
      unread,
      unreadLoading,
      loadFirstPage,
    ],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = () => useContext(NotificationsContext);
